import {
  Body,
  Button,
  CheckButton,
  Flex,
  Grid,
  Heading,
  SimpleGrid,
  Stack,
  TextInput,
  system,
  useTheme,
} from '@hover/blueprint';
import React, { useContext, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router';
import { type InferType } from 'yup';

import { OnboardingFormFooter } from 'src/modules/onboarding/components/OnboardingFormFooter';
import { StyledCheck } from 'src/modules/onboarding/components/StyledCheck';
import { makeOrgDescriptionValidationSchema } from 'src/modules/onboarding/validations/makeValidationSchemas';
import { useIsMobileSize } from 'src/modules/renderings/hooks';
import { Form as SharedForm } from 'src/shared/components/forms';
import {
  TranslationNamespace,
  useEffectOnMount,
  useTranslate,
} from 'src/shared/hooks';
import { Route as Routes } from 'src/shared/route';
import { buttonPressed, pageViewed } from 'src/shared/telemetry';

import {
  OrgConstructionDescriptions,
  OrgInsuranceDescriptions,
} from '../validations/validations';

import { OnboardingContext } from './OnboardingContext';

export type UserOrgFormData = InferType<
  ReturnType<typeof makeOrgDescriptionValidationSchema>
>;

interface OrgFormData {
  // Must be an array for react hook form to work correctly, but should only ever be 1 value
  orgDescription?: (OrgConstructionDescriptions | OrgInsuranceDescriptions)[];
  customDescription?: string;
}

interface UserOrgDescriptionFormFieldsProps {
  skip: () => void;
}

const UserOrgDescriptionFormFields = ({
  skip,
}: UserOrgDescriptionFormFieldsProps) => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const { breakpoints } = useTheme();

  const isMobile = useIsMobileSize();

  const [contextState] = useContext(OnboardingContext);

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { setValue, register } = useFormContext();
  const [checkedItem, setCheckedItem] = useState<string | undefined>(undefined);
  const [otherOpen, setOtherOpen] = useState(false);

  const onCheckDescription = (val: string) => {
    setOtherOpen(false);
    if (checkedItem === val) {
      setCheckedItem(undefined);
      setValue('orgDescription', undefined);
    } else {
      setCheckedItem(val);
      setValue('orgDescription', val);
    }
  };

  const orgConstructionDescriptionOptions = [
    {
      value: OrgConstructionDescriptions.RESTORATION_AND_MITIGATION,
      text: t('onboarding.restorationAndMitigation'),
    },
    {
      value: OrgConstructionDescriptions.CONTRACTOR,
      text: t('onboarding.contractor'),
    },
    {
      value: OrgConstructionDescriptions.CUSTOM_HOME_BUILDER,
      text: t('onboarding.customHomeBuilder'),
    },
    {
      value: OrgConstructionDescriptions.ARCHITECT_ENGINEER,
      text: t('onboarding.architectEngineer'),
    },
    {
      value: OrgConstructionDescriptions.DESIGNER,
      text: t('onboarding.designer'),
    },
    {
      value: OrgConstructionDescriptions.DISTRIBUTOR,
      text: t('onboarding.distributor'),
    },
    {
      value: OrgConstructionDescriptions.MANUFACTURER,
      text: t('onboarding.manufacturer'),
    },
  ];

  const orgInsuranceDescriptionOptions = [
    {
      value: OrgInsuranceDescriptions.NATIONAL_CARRIER,
      text: t('onboarding.nationalCarrier'),
    },
    {
      value: OrgInsuranceDescriptions.REGIONAL_CARRIER,
      text: t('onboarding.regionalCarrier'),
    },
    {
      value: OrgInsuranceDescriptions.FULL_SERVICE_ADJUSTING_FIRM,
      text: t('onboarding.fullServiceAdjustingFirm'),
    },
    {
      value: OrgInsuranceDescriptions.THIRD_PARTY_LADDER_ASSIST_FIRM,
      text: t('onboarding.thirdPartyLadderAssistFirm'),
    },
    {
      value: OrgInsuranceDescriptions.INDEPENDENT_ADJUSTER,
      text: t('onboarding.independentAdjusterInspector'),
    },
    {
      value: OrgInsuranceDescriptions.PUBLIC_ADJUSTING_FIRM,
      text: t('onboarding.publicAdjustingFirm'),
    },
    {
      value: OrgInsuranceDescriptions.UNDERWRITING_INSPECTOR,
      text: t('onboarding.underwritingInspector'),
    },
  ];

  const orgDescriptionOptions =
    contextState.orgType === 'insurance'
      ? orgInsuranceDescriptionOptions
      : orgConstructionDescriptionOptions;

  return (
    <Stack
      direction={{ base: 'column', tablet: 'row' }}
      flex={1}
      flexWrap="nowrap"
      justifyContent={{ base: 'start', tablet: 'space-around' }}
      marginTop={300}
      maxWidth={{ base: 'auto', tablet: breakpoints.width500 }}
      spacing={0}
      width={1}
    >
      <Flex
        flexBasis={{ base: 'auto', tablet: 750 }}
        flexGrow="1"
        flexShrink="1"
        mb={{ base: 400, tablet: 0 }}
      >
        <Flex flexDir="column" maxW={750}>
          <Heading
            mb={{ base: 400, tablet: 200 }}
            size={{ base: 600, tablet: 800 }}
            testId="user-org-description-heading"
          >
            {t('onboarding.howDoYouDescribe')}
          </Heading>
          <Body color="neutral.600" mt={0}>
            {t('onboarding.selectTheOption')}
          </Body>
        </Flex>
      </Flex>
      <Flex
        alignItems="center"
        flexBasis={750}
        flexDir="column"
        flexGrow="2"
        flexShrink="0"
      >
        <SimpleGrid
          columns={{ base: 1, tablet: 2 }}
          height="fit-content"
          spacingX={500}
          spacingY={300}
          width={1}
        >
          {orgDescriptionOptions.map(option => (
            <CheckButton
              isChecked={checkedItem === option.value}
              key={option.value}
              name="orgDescription"
              onChange={() => {
                onCheckDescription(option.value);
              }}
              overflowWrap="break-word"
              ref={register}
              size="large"
              textAlign="center"
              value={option.value}
              whiteSpace="break-spaces"
            >
              <system.span alignItems="center" display="inline-flex">
                {option.text}
                <StyledCheck isChecked={checkedItem === option.value} />
              </system.span>
            </CheckButton>
          ))}
          <CheckButton
            isChecked={otherOpen}
            onChange={() => {
              setCheckedItem(undefined);
              setOtherOpen(!otherOpen);
            }}
            size="large"
          >
            <system.span alignItems="center" display="inline-flex">
              {t('onboarding.other')}
              <StyledCheck isChecked={otherOpen} />
            </system.span>
          </CheckButton>
        </SimpleGrid>
        {otherOpen ? (
          <Controller
            defaultValue=""
            name="customDescription"
            render={props => (
              <TextInput
                bgColor="neutral.100"
                borderColor="neutral.100"
                borderRadius="15px"
                mt={500}
                onChange={val => props.onChange(val)}
                placeholder={t('onboarding.describeYourBusinessHere')}
              />
            )}
          />
        ) : (
          !isMobile && (
            <Button mt={600} onClick={skip} variant="ghost" width="fit-content">
              {t('onboarding.skip')}
            </Button>
          )
        )}
      </Flex>
    </Stack>
  );
};

export const UserOrgDescription = () => {
  const [, contextDispatch] = useContext(OnboardingContext);
  const validationSchema = makeOrgDescriptionValidationSchema();
  const initialValues = { orgDescription: [] };

  const history = useHistory();
  const { search } = useLocation();

  const navigateToUsage = () =>
    history.push(`${Routes.SIGN_UP_ONBOARDING.path}/usage${search}`);

  const skip = () => {
    buttonPressed({
      feature: 'onboarding_reskin',
      page_or_screen_name: 'Onboarding-persona_input',
      button_text: 'Skip',
    });
    navigateToUsage();
  };

  useEffectOnMount(() => {
    pageViewed({
      page_or_screen_name: 'Onboarding-persona',
      feature: 'onboarding_reskin',
    });
  });

  const onSubmit = (formData: OrgFormData) => {
    const { orgDescription } = formData;
    buttonPressed({
      feature: 'onboarding_reskin',
      page_or_screen_name: 'Onboarding-persona_input',
      button_text: 'Next',
      // @ts-ignore
      selection: orgDescription,
    });
    contextDispatch(formData);
    navigateToUsage();
  };

  return (
    <Grid alignItems="stretch" flex={1} width={1}>
      <SharedForm<UserOrgFormData>
        alignItems="center"
        display="flex"
        height={1}
        initialValues={initialValues}
        onSubmit={onSubmit}
        transparentFieldset
        validationSchema={validationSchema}
        w={1}
      >
        <UserOrgDescriptionFormFields skip={skip} />
        <OnboardingFormFooter hideBackButton hideForMobile={false} />
      </SharedForm>
    </Grid>
  );
};
